import type { EventType } from "@/events/events";
import { Task } from "@lit/task";
import { ReactiveController, ReactiveControllerHost } from "lit";

type NodeEvent = EventType | undefined;

type EventPipelineHost = ReactiveControllerHost & EventTarget & {
  _eventParent?     : NodeEvent;
  _eventDom?        : NodeEvent;
  _event?           : NodeEvent;
  ignoreEventsParent: boolean;
  autoForward       : boolean;
};

export class EventPipelineController implements ReactiveController {
  private host: EventPipelineHost;

  private _prevDomEventRef?   : NodeEvent;
  private _prevParentEventRef?: NodeEvent;
  private _prevIgnoreParent?  : boolean;

  private _syncTask    : Task<unknown[], void>;
  private _forwardTask : Task<unknown[], void>;
  private _forwardEvent: (e?: NodeEvent) => void;
  private _notifyParent: () => void;

  constructor(host: EventPipelineHost, forwardEvent: (e?: NodeEvent) => void, notifyParent: () => void) {
    this.host = host;
    this._forwardEvent = forwardEvent;
    this._notifyParent = notifyParent;
    host.addController(this);

    this._syncTask = new Task(host, {
      task: async ([fromParent, fromDom, ignoreParent]) => {
        const dom = fromDom as NodeEvent;
        const parent = fromParent as NodeEvent;

        const domChanged = !!dom && dom !== this._prevDomEventRef;
        const parentChanged = !!parent && parent !== this._prevParentEventRef;
        const ignoreChanged = ignoreParent !== this._prevIgnoreParent;

        if (!domChanged && !parentChanged && !ignoreChanged) return;

        this._prevIgnoreParent = ignoreParent;
        if (domChanged) this._prevDomEventRef = dom;
        if (parentChanged) this._prevParentEventRef = parent;

        if (ignoreParent) {
          if (domChanged) {
            this.host._event = dom;
          }
          return;
        }

        if (domChanged) {
          this.host._event = dom;
          return;
        }

        if (parentChanged) {
          this.host._event = parent;
          return;
        }

        // If only the ignore flag changed, adopt parent event (if any).
        if (!ignoreParent && parent) {
          this.host._event = parent;
        }
      },
      args: () => [this.host._eventParent, this.host._eventDom, this.host.ignoreEventsParent]
    });

    this._forwardTask = new Task(host, {
      task: async ([evt, auto]) => {
        if (!evt || !auto) return;
        this._forwardEvent(evt as NodeEvent);
        this._notifyParent();
      },
      args: () => [this.host._event, this.host.autoForward]
    });
  }

  get syncComplete() {
    return this._syncTask.taskComplete;
  }

  get forwardComplete() {
    return this._forwardTask.taskComplete;
  }

  hostConnected() {
    // no-op
  }

  hostDisconnected() {
    // no-op
  }

  hostUpdate() {
    // no-op
  }

  hostUpdated() {
    // no-op
  }
}
